import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@components/forms/Button/Button";
import Card from "@components/ui/Card/Card";
import Group from "@ltypes/group";
import TextField from "@components/forms/TextField/TextField";
import RoundedCheckbox from "@components/forms/RoundedCheckbox/RoundedCheckbox";
import Switch from "@components/forms/Switch/Switch";
import GradientColor from "@components/forms/GradientColor/GradientColor";

type GroupFormProps = {
    group?: Group,
    onSubmit: (name: string, color: number, allDay: boolean, startTime: string, stopTime: string, repeat: Array<number>) => Promise<any>,
    onCancel: () => void,
};

function GroupForm(props: GroupFormProps) {
    const [name, setName] = useState<string>('');
    const [color, setColor] = useState<number>(0);
    const [allDay, setAllDay] = useState<boolean>(false);
    const [startTime, setStartTime] = useState<string>('00:00');
    const [stopTime, setStopTime] = useState<string>('01:00');
    const [repeat, setRepeat] = useState<Array<number>>([0, 1, 2, 3, 4, 5, 6]);
    const { t } = useTranslation();

    const handleCancel = () => {
        props.onCancel();
        setName('');
        setColor(0);
        setStartTime('00:00');
        setStopTime('01:00');
        setRepeat([0, 1, 2, 3, 4, 5, 6]);
        setAllDay(false);
    };

    const handleSubmit = () => {
        props.onSubmit(name, color, allDay, startTime, stopTime, repeat).then(handleCancel).catch(() => {});
    };

    const handleChangeAllDay = (newValue: boolean): void => {
        setAllDay(newValue);
        setStartTime('00:00');
        setStopTime('01:00');
    };

    useEffect(() => {
        setName(props.group?.name ?? '');
        setColor(props.group?.color ?? 0);
        setAllDay(props.group?.all_day ?? false);
        setStartTime(props.group?.start_time ?? '00:00');
        setStopTime(props.group?.stop_time ?? '01:00');
        setRepeat(props.group?.repeat ? props.group?.repeat.map(Number) : [0, 1, 2, 3, 4, 5, 6]);
    }, [props.group]);

    return (
        <>
            <h2 className="mb-2">{props.group ? t('page.groups.update_group_of_scenes', { name: props.group.name }) : t('page.groups.create_new_group_of_scenes')}</h2>
            <Card>
                <div className="row mb-3 g-3">
                    <div className="col-12">
                        <TextField label={t('page.groups.input.name.label')} value={name} placeholder={t('page.groups.input.name.placeholder')} onChange={setName}/>
                    </div>
                </div>
                <div className="row mb-3 g-3">
                    <div className="col-12">
                        <Switch label={t('page.groups.input.all_day')} onChange={handleChangeAllDay} checked={allDay}/>
                    </div>
                </div>
                {!allDay && (
                    <div className="row mb-3 g-3">
                        <div className="col-6">
                            <TextField label={t('page.groups.input.start_time')} type="time" value={startTime} onChange={setStartTime}/>
                        </div>

                        <div className="col-6">
                            <TextField label={t('page.groups.input.stop_time')} type="time" value={stopTime} onChange={setStopTime}/>
                        </div>
                    </div>
                )}
                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <RoundedCheckbox label={t('page.groups.input.repeat')} items={[
                            {label: t('page.groups.repeat_days.monday'), value: 1},
                            {label: t('page.groups.repeat_days.tuesday'), value: 2},
                            {label: t('page.groups.repeat_days.wednesday'), value: 3},
                            {label: t('page.groups.repeat_days.thursday'), value: 4},
                            {label: t('page.groups.repeat_days.friday'), value: 5},
                            {label: t('page.groups.repeat_days.saturday'), value: 6},
                            {label: t('page.groups.repeat_days.sunday'), value: 0},
                        ]} value={repeat} onChange={setRepeat}/>
                    </div>
                    <div className="col-12 col-md-6">
                        <GradientColor label={t('page.groups.input.color')} value={color} onChange={setColor}/>
                    </div>
                </div>

                <div className="row mt-3 gx-2 justify-content-end">
                    <div className="col-auto">
                        <Button className="secondary" onClick={handleCancel}>{t('text.cancel')}</Button>
                    </div>
                    <div className="col-auto">
                        <Button onClick={handleSubmit} disabled={name.trim() === ''}>{props.group ? t('text.update') : t('text.create')}</Button>
                    </div>
                </div>
            </Card>
        </>
    );
}

export default GroupForm;
